import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AlertCircle, Loader2, User } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { mapTransferIdToApiPathParam } from 'api/warehouse/fcTransfers';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { AcceptTransferHeader } from 'components/warehouse/components/fcTransfers/acceptTransfer/AcceptTransferHeader';
import { AcceptTransferLineItemsTable } from 'components/warehouse/components/fcTransfers/acceptTransfer/AcceptTransferLineItemsTable';
import { buildReceiptLinePayloadFromDraft } from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineReceiptDraft';
import { AcceptTransferLineReceiveModal } from 'components/warehouse/components/fcTransfers/acceptTransfer/AcceptTransferLineReceiveModal';
import { AcceptTransferSummaryCard } from 'components/warehouse/components/fcTransfers/acceptTransfer/AcceptTransferSummaryCard';
import { FC_TRANSFER_DIRECTION } from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import {
    fcTransferReceiveLineBadgeClassNames,
    getFcTransferReceiveLineBadgeBucket,
} from 'components/warehouse/components/fcTransfers/fcTransferReceiveBadgeStyles';
import {
    useFCTransferById,
    useSaveFCTransferReceiptLine,
} from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { useFCTransfersBackHref } from 'components/warehouse/components/fcTransfers/useFCTransfersBackHref';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';

const FC_TRANSFERS_INBOUND_LIST_HREF = `/warehouse/fc-transfers?direction=${FC_TRANSFER_DIRECTION.INBOUND}`;

function deriveLineAggregates(transfer) {
    const raw = transfer?.items;
    const items = Array.isArray(raw) ? raw : [];
    let pending = 0;
    let inProgress = 0;
    let saved = 0;
    for (const row of items) {
        const bucket = getFcTransferReceiveLineBadgeBucket(row?.status);
        if (bucket === 'pending') {
            pending += 1;
        } else if (bucket === 'inProgress') {
            inProgress += 1;
        } else if (bucket === 'saved') {
            saved += 1;
        }
    }
    return { pending, inProgress, saved, totalLines: items.length };
}

/**
 * Dedicated Accept Transfer page: loads transfer detail and renders receive shell (tech spec §3.2).
 */
export function ViewAcceptTransfer() {
    const { transferId } = useParams();
    const { user } = useContext(UserContext);
    const backHref = useFCTransfersBackHref({
        emptyFallback: FC_TRANSFERS_INBOUND_LIST_HREF,
    });

    const normalizedTransferId = mapTransferIdToApiPathParam(transferId);
    const isValidTransferId = normalizedTransferId !== undefined;

    const {
        data: transfer,
        error,
        isError,
        isFetching,
        isLoading,
        isPending,
    } = useFCTransferById({
        transferId,
        enabled: Boolean(transferId) && isValidTransferId,
    });

    const aggregates = useMemo(() => deriveLineAggregates(transfer), [transfer]);

    const [receiveModalItem, setReceiveModalItem] = useState(null);
    const [receiveModalApiError, setReceiveModalApiError] = useState(null);

    const saveLineMutation = useSaveFCTransferReceiptLine();

    const savePendingItemId = saveLineMutation.isPending ? (saveLineMutation.variables?.transferItemId ?? null) : null;

    const receiveModalOpen = receiveModalItem !== null && receiveModalItem !== undefined;

    const activeModalItemId = receiveModalItem?.id ?? receiveModalItem?.transferItemId;
    const isSavingActiveLine =
        saveLineMutation.isPending &&
        activeModalItemId !== undefined &&
        activeModalItemId !== null &&
        String(saveLineMutation.variables?.transferItemId) === String(activeModalItemId);

    const handleOpenReceiveModal = useCallback((item) => {
        setReceiveModalApiError(null);
        setReceiveModalItem(item);
    }, []);

    const handleReceiveModalOpenChange = useCallback((open) => {
        if (!open) {
            setReceiveModalItem(null);
            setReceiveModalApiError(null);
        }
    }, []);

    const handleReceiveModalSubmit = useCallback(
        async (draft) => {
            const item = receiveModalItem;
            const id = item?.id ?? item?.transferItemId;
            if (id === undefined || id === null) {
                return;
            }
            setReceiveModalApiError(null);
            try {
                await saveLineMutation.mutateAsync({
                    payload: buildReceiptLinePayloadFromDraft(draft),
                    transferId,
                    transferItemId: id,
                });
                setReceiveModalItem(null);
            } catch (e) {
                const message = e instanceof Error ? e.message : 'Failed to save line';
                setReceiveModalApiError(message);
            }
        },
        [receiveModalItem, saveLineMutation, transferId],
    );

    const lineItems = useMemo(() => {
        const raw = transfer?.items;
        return Array.isArray(raw) ? raw : [];
    }, [transfer?.items]);

    const operatorLabel = useMemo(() => {
        const email = typeof user?.email === 'string' ? user.email.trim() : '';
        const name = typeof user?.name === 'string' ? user.name.trim() : '';
        return email || name || '—';
    }, [user?.email, user?.name]);

    const transferPk = transfer?.id ?? transfer?.transferId;
    const hasResolvedTransfer = transferPk !== undefined && transferPk !== null && String(transferPk).trim() !== '';
    const isTransferLoading = !hasResolvedTransfer && (isLoading || isPending || isFetching || !isError);

    if (!isValidTransferId) {
        return (
            <div className="flex flex-col gap-4 px-4 py-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Invalid transfer</AlertTitle>
                    <AlertDescription>
                        The URL does not contain a valid FC transfer id. Use a transfer opened from FC Transfers.
                    </AlertDescription>
                </Alert>
                <Button variant="outline" asChild>
                    <Link to={backHref}>Back to FC Transfers</Link>
                </Button>
            </div>
        );
    }

    if (isTransferLoading) {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4">
                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">Loading transfer…</p>
            </div>
        );
    }

    if (!isTransferLoading && isError) {
        return (
            <div className="flex flex-col gap-4 px-4 py-6">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Could not load transfer</AlertTitle>
                    <AlertDescription>{error?.message ?? 'Transfer not found or unavailable.'}</AlertDescription>
                </Alert>
                <Button variant="outline" asChild>
                    <Link to={backHref}>Back to FC Transfers</Link>
                </Button>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col px-4 pb-8 pt-4">
            <AcceptTransferLineReceiveModal
                isSubmitting={isSavingActiveLine}
                item={receiveModalItem}
                open={receiveModalOpen}
                onClearSubmitError={() => setReceiveModalApiError(null)}
                onOpenChange={handleReceiveModalOpenChange}
                onSubmit={handleReceiveModalSubmit}
                submitError={receiveModalApiError}
            />

            <ViewEntityPage
                header={
                    <>
                        <AcceptTransferHeader backHref={backHref} transfer={transfer} />
                        <div className="pt-2">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-muted/50 px-4 py-2.5">
                                <div className="flex min-w-0 items-center gap-2">
                                    <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                    <span className="text-sm text-muted-foreground">Logged in as:</span>
                                    <span className="text-sm font-semibold text-foreground break-all">
                                        {operatorLabel}
                                    </span>
                                </div>
                                <div className="hidden h-5 w-px shrink-0 bg-border sm:block" aria-hidden />
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Lines:</span>
                                    <div className="flex flex-wrap items-center gap-2" aria-label="Line status counts">
                                        <Badge
                                            variant="outline"
                                            className={fcTransferReceiveLineBadgeClassNames.pending}
                                        >
                                            {aggregates.pending} pending
                                        </Badge>
                                        <Badge
                                            variant="outline"
                                            className={fcTransferReceiveLineBadgeClassNames.inProgress}
                                        >
                                            {aggregates.inProgress} in progress
                                        </Badge>
                                        <Badge variant="outline" className={fcTransferReceiveLineBadgeClassNames.saved}>
                                            {aggregates.saved} saved
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
                primaryInformationCard={<AcceptTransferSummaryCard transfer={transfer} />}
                additionalInformationCards={
                    <AcceptTransferLineItemsTable
                        items={lineItems}
                        savePendingItemId={savePendingItemId}
                        onOpenReceiveModal={handleOpenReceiveModal}
                    />
                }
            />
        </div>
    );
}

ViewAcceptTransfer.displayName = 'ViewAcceptTransfer';
